// i18next bootstrap. Imported by src/main.tsx BEFORE createRoot()
// so detection + resource load complete synchronously — no flicker.
// Mirrors rabotix-eazl/rabotix web-ui's pattern; preserves yuklar's
// existing cookie/localStorage key "yuklar_language" so user
// language choices survive the migration.

import i18n from "i18next";
import ICU from "i18next-icu";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "../../../../shared/locales/en.json";
import ru from "../../../../shared/locales/ru.json";
import tr from "../../../../shared/locales/tr.json";
import uzCyr from "../../../../shared/locales/uz_cyr.json";
import uzLat from "../../../../shared/locales/uz_lat.json";

// i18next-icu enables ICU MessageFormat parsing in locale entries:
//   - Single-brace interpolation `{n}` (matches the existing next-intl-era format)
//   - Plurals: `{count, plural, one {1 day} other {# days}}`
// Drop after a follow-up rewrite to react-i18next's `_one`/`_other` convention
// (then we can drop i18next-icu and use stock interpolation).

// Resource shape supports the three call patterns the codebase uses:
//   - `useTranslation("auth").t("signIn")` → namespace "auth"
//   - `useTranslation("admin.loadApprovals").t("status.all")` → namespace
//     "admin.loadApprovals" (every nested object path is registered as its
//     own namespace, mirroring how next-intl's `useTranslations("a.b")`
//     scoped into a sub-tree)
//   - `useTranslation().t("dashboard.actions.openLoad")` (bare, no scope)
//     → default "translation" namespace carries the full tree, so dot-
//     notation paths resolve like next-intl's unscoped `useTranslations()`.
const collectNamespaces = (
  obj: Record<string, unknown>,
  prefix: string,
  out: Record<string, unknown>,
) => {
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
      continue;
    const path = prefix ? `${prefix}.${key}` : key;
    out[path] = value;
    collectNamespaces(value as Record<string, unknown>, path, out);
  }
};

const wrap = (messages: Record<string, unknown>) => {
  const namespaces: Record<string, unknown> = {};
  collectNamespaces(messages, "", namespaces);
  return { ...namespaces, translation: messages };
};

void i18n
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: wrap(en),
      ru: wrap(ru),
      tr: wrap(tr),
      "uz-Cyrl": wrap(uzCyr),
      "uz-Latn": wrap(uzLat),
    },
    fallbackLng: "en",
    supportedLngs: ["en", "ru", "tr", "uz-Cyrl", "uz-Latn"],
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    returnNull: false,
    parseMissingKeyHandler: (key: string) => key,
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      lookupLocalStorage: "yuklar_language",
      lookupCookie: "yuklar_language",
      caches: ["localStorage", "cookie"],
    },
  });

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

export { i18n };
